import React, { useState } from "react";
import {
  Box,
  Slider,
  Typography,
  FormControl,
  Select,
  MenuItem,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { SelectAppDataProps } from "../../../../Types/Workflow-Types";
import { rangeStyles } from "../../../TextSuite/Components/TextSuitesBody";
import SettingItem from "../Shared/SettingItem";
import OutputSettingText from "../Shared/OutputSettingText";
import InfoItem from "../Shared/InfoItem";
import WorkflowSettingTabs from "../Shared/WorkflowSettingTabs";

const SummarizerSetting = ({
  activeItemData,
  sliderValue,
  isFirstStep,
  selectedFromDropdownItem,
  setSelectedFromDropdownItem,
  setSliderValue,
  readOnly,
  canShow,
}: SelectAppDataProps) => {
  const [value, setValue] = React.useState(0);
  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };
  const [menuOpenFrom, setMenuOpenFrom] = useState(false);

  const handleChangeFrom = (value: string) => {
    setSelectedFromDropdownItem?.(value);
  };

  const handleSliderChange = (event: Event, newValue: number | number[]) => {
    setSliderValue?.(newValue as number);
  };

  return canShow ? (
    !readOnly ? (
      <Box className="w-100 d-flex flex-column justify-content-center">
        <Box
          sx={{
            flexGrow: 1,
            justifyContent: "space-between",
            display: "flex",
            gap: "8px",
            height: 260,
          }}
        >
          <WorkflowSettingTabs value={value} handleChange={handleChange} />
          <SettingItem value={value} index={0}>
            <InfoItem
              header="Text"
              inputText={
                !isFirstStep
                  ? "Text will be added from the previous step."
                  : "Text will be added at runtime."
              }
            />
          </SettingItem>
          <SettingItem value={value} index={1}>
            <Box className="w-100">
              <Box
                className="conatiner-setting-item"
                sx={{ display: "flex", alignItems: "center" }}
              >
                <label className="me-3 label-name">Mode</label>

                <FormControl className="FormControl-appsetting" sx={{ m: 1 }}>
                  <Select
                    disabled={readOnly}
                    displayEmpty
                    onOpen={() => setMenuOpenFrom(true)}
                    onClose={() => setMenuOpenFrom(false)}
                    open={menuOpenFrom}
                    renderValue={(selected) =>
                      menuOpenFrom
                        ? "Select mode"
                        : (selected as string) || selectedFromDropdownItem
                    }
                    inputProps={{ "aria-label": "Without label" }}
                    className="app-selector-setting selected-lang-From"
                    IconComponent={(props) => (
                      <ExpandMoreIcon
                        {...props}
                        sx={{ fill: "var(--Main_icons_color)" }}
                      />
                    )}
                    sx={{
                      border: "none",
                      opacity: menuOpenFrom ? "0.5" : "1",
                      color: "var(--Main_icons_color)",
                      "&.Mui-focused fieldset": {
                        border: "none",
                        outline: "0",
                      },
                    }}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          minWidth: "18.7%",
                          top: "33%",
                          maxHeight: "163px",
                          backgroundColor: "var(--A_backgroundColor77)",
                          borderRadius: "8px",
                          boxShadow: "none",
                          border: "1px solid var(--A_backgroundColor80)",
                        },
                      },
                    }}
                  >
                    {activeItemData?.selectorOptions?.map(
                      (lang: string, i: number) => (
                        <MenuItem
                          value={lang}
                          key={i}
                          className="selected-lang"
                          sx={{
                            color: "var(--Main_icons_color)",
                            fontSize: "12px",
                            minHeight: "auto",
                            "&:hover": {
                              backgroundColor: "var(--m-background44)",
                              textDecoration: "none",
                            },
                            "&.Mui-selected": {
                              backgroundColor: "var(--M-background98)",
                            },
                            "&.Mui-selected:hover": {
                              backgroundColor: "var(--M-background98)",
                              textDecoration: "none",
                            },
                          }}
                          onClick={() => handleChangeFrom(lang)}
                        >
                          {lang}
                        </MenuItem>
                      )
                    )}
                  </Select>
                </FormControl>
              </Box>

              {selectedFromDropdownItem === "Paragraph" && (
                <Box
                  className="conatiner-setting-item"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    width: "100%",
                    marginTop: "1.3rem",
                  }}
                >
                  <label className="me-3 label-name">Paragraph length:</label>
                  <Box className="paragraph-range ms-2">
                    <Typography className="paragraph-range-text">
                      Short
                    </Typography>
                    <Slider
                      disabled={readOnly}
                      aria-label="Paragraph Length"
                      value={sliderValue}
                      valueLabelDisplay="off"
                      step={1}
                      marks
                      min={0}
                      max={3}
                      sx={rangeStyles}
                      onChange={handleSliderChange}
                    />
                    <Typography className="paragraph-range-text">
                      Long
                    </Typography>
                  </Box>
                </Box>
              )}
            </Box>
          </SettingItem>
          <SettingItem value={value} index={2}>
            <OutputSettingText readOnly={readOnly} />
          </SettingItem>
        </Box>
      </Box>
    ) : (
      <Box className="w-100 d-flex flex-column justify-content-center">
        <ul className="w-100 workflow-list-readOnly">
          {/* input Type */}
          <li className="w-100 workflow-list-item-readOnly">
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexWrap: "wrap",
              }}
            >
              <Typography>Input type:</Typography>
              <Typography
                sx={{
                  fontFamily: "Roboto",
                  fontWeight: "400",
                  fontSize: "14px",
                  lineHeight: "25px",
                  letterSpacing: "0.1px",
                  textDecoration: "underline",
                  color: "var(--Main_icons_color)",
                  opacity: "0.7",
                  textAlign: "start",
                }}
              >
                {!isFirstStep
                  ? "Text will be added from the previous step."
                  : "Text will be added at runtime."}
              </Typography>
            </Box>
          </li>
          {/* settings */}
          <li className="w-100 workflow-list-item-readOnly">
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-start",
                justifyContent: "space-between",
                flexDirection: "column",
              }}
            >
              <Typography>Settings:</Typography>
              <Box className="container-workflow-setting-redaOnly">
                {/* input language */}
                <Box
                  className="workflow-parameter-item-readOnly"
                  sx={{
                    paddingBottom:
                      selectedFromDropdownItem === "Paragraph" ? "16px" : "",
                    borderBottom:
                      selectedFromDropdownItem === "Paragraph"
                        ? "1px solid var(--A_backgroundColor102)"
                        : "none",
                  }}
                >
                  <Typography className="paramter-value-setting">
                    Mode:
                  </Typography>
                  <Typography className="paramter-value-setting">
                    {selectedFromDropdownItem}
                  </Typography>
                </Box>
                {/* output language */}
                {selectedFromDropdownItem === "Paragraph" && (
                  <Box
                    className="workflow-parameter-item-readOnly"
                    sx={{
                      paddingTop: "16px",
                    }}
                  >
                    <Typography className="paramter-value-setting">
                      Paragraph length:
                    </Typography>
                    <Typography className="paramter-value-setting">
                      {sliderValue}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          </li>
          {/* output Type */}
          <li className="w-100 workflow-list-item-readOnly">
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                flexWrap: "wrap",
              }}
            >
              <Typography>Output type:</Typography>
              <OutputSettingText readOnly={readOnly} />
            </Box>
          </li>
        </ul>
      </Box>
    )
  ) : null;
};

export default SummarizerSetting;
